import React, { useState } from 'react';
import styled from 'styled-components';
import { MdNotifications, MdClose } from 'react-icons/md';

const UpcomingContainer = styled.section`
  max-width: 1400px;
  margin: 0 auto;
  padding: 6rem 2rem 4rem;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 4rem 1rem 2rem;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  width: 100%;
`;

const Subtitle = styled.span`
  color: var(--color-champagne);
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.2em;
  display: block;
  margin-bottom: 0.75rem;
`;

const Title = styled.h2`
  font-family: 'Cinzel', serif;
  font-size: clamp(1.75rem, 4vw, 2.5rem);
  font-weight: 700;
  letter-spacing: 0.05em;
  color: var(--color-pearl);
  
  &::after {
    content: '';
    display: block;
    width: 60px;
    height: 1px;
    background: var(--color-champagne);
    margin: 1.25rem auto 0;
    opacity: 0.6;
  }
`;

const UpcomingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2.5rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 680px) {
    grid-template-columns: 1fr;
  }
`;

const UpcomingCard = styled.div`
  background: linear-gradient(135deg, rgba(20, 20, 20, 0.7) 0%, rgba(12, 12, 12, 0.9) 100%);
  border: 1px solid rgba(255, 255, 255, 0.03);
  border-radius: 16px;
  overflow: hidden;
  position: relative;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  height: 100%;
  
  &:hover {
    transform: translateY(-6px);
    border-color: rgba(212, 175, 55, 0.25);
    box-shadow: 
      0 15px 30px rgba(0, 0, 0, 0.6),
      0 0 20px rgba(212, 175, 55, 0.08);
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 125%; /* 4:5 Aspect Ratio for premium collectibles */
  overflow: hidden;
  background: #111;
  cursor: pointer;
`;

const MainImage = styled.img<{ isHovered: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  transform: ${props => props.isHovered ? 'scale(1.09)' : 'scale(1.05)'};
`;

const RibbonBadge = styled.div`
  position: absolute;
  top: 1rem;
  left: 1rem;
  background: var(--color-champagne);
  color: var(--color-obsidian);
  font-weight: 800;
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  padding: 0.35rem 0.75rem;
  border-radius: 4px;
  box-shadow: 0 4px 10px rgba(212, 175, 55, 0.25);
  z-index: 2;
`;

const HoverOverlay = styled.div<{ isVisible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${props => props.isVisible ? 1 : 0};
  transition: opacity 0.4s ease;
  z-index: 4;
`;

const QuickViewBtn = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 0.6rem 1.2rem;
  border-radius: 8px;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  backdrop-filter: blur(8px);
  cursor: pointer;
  
  &:hover {
    background: white;
    color: black;
  }
`;

const InfoSection = styled.div`
  padding: 1rem;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

const BrandLabel = styled.span`
  color: var(--color-champagne);
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  margin-bottom: 0.25rem;
`;

const ProductTitle = styled.h3`
  font-family: 'Cinzel', serif;
  color: var(--color-pearl);
  font-size: 1.05rem;
  font-weight: 700;
  margin-bottom: 0.15rem;
  letter-spacing: 0.02em;
`;

const ProductSubtitle = styled.span`
  color: var(--color-slate-400);
  font-size: 0.8rem;
  font-weight: 500;
  margin-bottom: 0.6rem;
  display: block;
`;

const Description = styled.p`
  color: var(--color-slate-400);
  font-size: 0.8rem;
  line-height: 1.45;
  margin-bottom: 1rem;
  flex-grow: 1;
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.03);
`;

const StatusTag = styled.span`
  color: var(--color-champagne);
  font-weight: 700;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const NotifyBtn = styled.button`
  background: transparent;
  color: var(--color-pearl);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  padding: 0.5rem 0.85rem;
  font-size: 0.8rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: var(--color-champagne);
    color: var(--color-obsidian);
    border-color: var(--color-champagne);
  }
`;

/* Modal Styles */
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(10px);
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const ModalContent = styled.div`
  background: linear-gradient(135deg, #161616 0%, #0b0b0b 100%);
  border: 1px solid rgba(212, 175, 55, 0.25);
  border-radius: 24px;
  max-width: 1000px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  box-shadow: 0 30px 60px rgba(0, 0, 0, 0.8);
  
  @media (max-width: 850px) {
    grid-template-columns: 1fr;
  }
`;

const ModalCloseBtn = styled.button`
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(212, 175, 55, 0.2);
  color: var(--color-slate-300);
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  z-index: 10;
  cursor: pointer;
  
  &:hover {
    color: var(--color-champagne);
    background: rgba(255, 255, 255, 0.1);
  }
`;

const ModalGallery = styled.div`
  padding: 2.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  border-right: 1px solid rgba(255, 255, 255, 0.05);
  
  @media (max-width: 850px) {
    border-right: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
    padding: 1.5rem;
  }
`;

const ModalMainImageWrapper = styled.div`
  width: 100%;
  height: 400px;
  border-radius: 12px;
  border: 1px solid rgba(212, 175, 55, 0.15);
  background: rgba(0, 0, 0, 0.4);
  overflow: hidden;
  position: relative;
  
  @media (max-width: 480px) {
    height: 250px;
  }
`;

const ModalMainImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  clip-path: inset(10px 4px 4px 4px);
`;

const ThumbnailContainer = styled.div`
  display: flex;
  gap: 0.75rem;
  overflow-x: auto;
  padding-bottom: 0.5rem;
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--color-champagne);
    border-radius: 2px;
  }
`;

const ModalThumbnail = styled.img<{ isActive: boolean }>`
  width: 70px;
  height: 70px;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid ${props => props.isActive ? 'var(--color-champagne)' : 'rgba(255, 255, 255, 0.1)'};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: var(--color-champagne);
  }
`;

const ModalDetails = styled.div`
  padding: 2.5rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  
  @media (max-width: 850px) {
    padding: 1.5rem;
  }
`;

const ModalTitle = styled.h2`
  font-family: 'Cinzel', serif;
  font-size: 1.8rem;
  font-weight: 700;
  color: var(--color-pearl);
  margin-bottom: 0.5rem;
  line-height: 1.2;
`;

const ModalSubtitle = styled.div`
  font-size: 0.9rem;
  color: var(--color-champagne);
  margin-bottom: 1.5rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const ModalDesc = styled.p`
  font-size: 0.9rem;
  line-height: 1.6;
  color: var(--color-slate-300);
  margin-bottom: 2rem;
`;

const SpecsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
  margin-bottom: 2rem;
  background: rgba(255, 255, 255, 0.02);
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.03);
`;

const SpecItem = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.85rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  padding-bottom: 0.4rem;
  
  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
  
  span.label {
    color: var(--color-slate-400);
    font-weight: 500;
  }
  span.value {
    color: var(--color-pearl);
    font-weight: 600;
  }
`;

const ModalActions = styled.div`
  margin-top: 1rem;
`;

const PreOrderAlertBtn = styled.button`
  width: 100%;
  background: var(--color-champagne);
  color: var(--color-obsidian);
  border: 1px solid var(--color-champagne);
  padding: 1rem;
  border-radius: 12px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 0.9rem;
  box-shadow: 0 10px 20px rgba(212, 175, 55, 0.2);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: transparent;
    color: var(--color-champagne);
    transform: translateY(-2px);
  }
`;

// Upcoming Products Data
const upcomingProducts = [
  {
    id: 1,
    brand: "SALAAR",
    title: "Deva (The Gladiator)",
    subtitle: "1:4 Scale Premium Statue",
    description: "A premium quarter-scale representation of Deva, capturing the rebel commander's raw intensity and battlefield stance.",
    scale: "1:4 Scale",
    material: "Polystone & Metallic Highlights",
    dimensions: "22\" H x 11.5\" W x 11\" D",
    defaultImageIndex: 1,
    images: [
      "/assets/upcoming/1.jpg",
      "/assets/upcoming/2.jpg",
      "/assets/upcoming/3.jpg"
    ]
  },
  {
    id: 2,
    brand: "SALAAR",
    title: "Vardharaja Mannaar",
    subtitle: "1:4 Scale Premium Statue",
    description: "Meticulously crafted statue depicting the formidable Vardharaja Mannaar, featuring signature sword and armor elements.",
    scale: "1:4 Scale",
    material: "Polystone & Sovereignty Emblem",
    dimensions: "22.5\" H x 12\" W x 11.5\" D",
    defaultImageIndex: 0,
    images: [
      "/assets/upcoming/4.jpg",
      "/assets/upcoming/5.jpg",
      "/assets/upcoming/6.jpg"
    ]
  },
  {
    id: 3,
    brand: "SALAAR",
    title: "The Battle of Khansaar",
    subtitle: "1:6 Scale Battle Diorama",
    description: "A high-intensity, limited edition diorama freezing the epic, blood-pounding final clash inside the city of Khansaar.",
    scale: "1:6 Scale Diorama",
    material: "Polystone & Custom Base",
    dimensions: "16.5\" H x 20\" W x 14\" D",
    defaultImageIndex: 2,
    images: [
      "/assets/upcoming/7.jpg",
      "/assets/upcoming/8.jpg",
      "/assets/upcoming/9.jpg"
    ]
  }
];

const Upcoming: React.FC = () => {
  const [hoveredCardId, setHoveredCardId] = useState<number | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [activeImageIndex, setActiveImageIndex] = useState<number>(0);

  const handleNotify = (productTitle: string) => {
    alert(`Pre-order alerts active for "${productTitle}"! We will email you once it goes live.`);
  };

  return (
    <UpcomingContainer id="upcoming">
      <SectionHeader>
        <Subtitle>EXCLUSIVE PREVIEWS</Subtitle>
        <Title>Upcoming Releases</Title>
      </SectionHeader>
      
      <UpcomingGrid>
        {upcomingProducts.map((product) => {
          const isHovered = hoveredCardId === product.id;
          return (
            <UpcomingCard 
              key={product.id}
              onMouseEnter={() => setHoveredCardId(product.id)}
              onMouseLeave={() => setHoveredCardId(null)}
            >
              <RibbonBadge>Upcoming</RibbonBadge>
              <ImageWrapper onClick={() => {
                setSelectedProduct(product);
                setActiveImageIndex(product.defaultImageIndex);
              }}>
                <MainImage src={product.images[product.defaultImageIndex]} alt={product.title} isHovered={isHovered} />
                <HoverOverlay isVisible={isHovered}>
                  <QuickViewBtn>Quick View</QuickViewBtn>
                </HoverOverlay>
              </ImageWrapper>
              
              <InfoSection>
                <BrandLabel>{product.brand}</BrandLabel>
                <ProductTitle>{product.title}</ProductTitle>
                <ProductSubtitle>{product.subtitle}</ProductSubtitle>
                <Description>{product.description}</Description>
                
                <ActionRow>
                  <StatusTag>Pre-Order Soon</StatusTag>
                  <NotifyBtn onClick={() => handleNotify(product.title)}>
                    {React.createElement(MdNotifications as React.ComponentType<any>)}
                    Notify Me
                  </NotifyBtn>
                </ActionRow>
              </InfoSection>
            </UpcomingCard>
          );
        })}
      </UpcomingGrid>

      {/* Quick View Modal */}
      {selectedProduct && (
        <ModalOverlay onClick={() => setSelectedProduct(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalCloseBtn onClick={() => setSelectedProduct(null)} aria-label="Close modal">
              {React.createElement(MdClose as React.ComponentType<any>)}
            </ModalCloseBtn>
            
            <ModalGallery>
              <ModalMainImageWrapper>
                <ModalMainImage src={selectedProduct.images[activeImageIndex]} alt={selectedProduct.title} />
              </ModalMainImageWrapper>
              {selectedProduct.images.length > 1 && (
                <ThumbnailContainer>
                  {selectedProduct.images.map((img: string, idx: number) => (
                    <ModalThumbnail 
                      key={idx} 
                      src={img} 
                      alt="" 
                      isActive={activeImageIndex === idx} 
                      onClick={() => setActiveImageIndex(idx)}
                    />
                  ))}
                </ThumbnailContainer>
              )}
            </ModalGallery>

            <ModalDetails>
              <ModalSubtitle>KIII TOYS STUDIOS • {selectedProduct.brand}</ModalSubtitle>
              <ModalTitle>{selectedProduct.title}</ModalTitle>
              <ModalSubtitle style={{ color: 'var(--color-slate-400)', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
                {selectedProduct.subtitle}
              </ModalSubtitle>
              <ModalDesc>{selectedProduct.description}</ModalDesc>
              
              <SpecsGrid>
                <SpecItem>
                  <span className="label">Format</span>
                  <span className="value">{selectedProduct.scale}</span>
                </SpecItem>
                <SpecItem>
                  <span className="label">Materials</span>
                  <span className="value">{selectedProduct.material}</span>
                </SpecItem>
                <SpecItem>
                  <span className="label">Dimensions</span>
                  <span className="value">{selectedProduct.dimensions}</span>
                </SpecItem>
              </SpecsGrid>

              <ModalActions>
                <PreOrderAlertBtn onClick={() => {
                  handleNotify(selectedProduct.title);
                  setSelectedProduct(null);
                }}>
                  {React.createElement(MdNotifications as React.ComponentType<any>)}
                  Activate Pre-Order Notification
                </PreOrderAlertBtn>
              </ModalActions>
            </ModalDetails>
          </ModalContent>
        </ModalOverlay>
      )}
    </UpcomingContainer>
  );
};

export default Upcoming;
